import { action, observable, computed } from 'mobx'
import { required, validationContext } from 'validx'
import { type Job, type AmendmentVisibility } from '@/features/jobs/Job'
import { mustHaveMaxLength } from '@/utils/validx-util'
import { whileActive } from '@/utils/rx-util'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type JobStore } from '@/features/jobs/JobStore'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { task } from 'mobx-task'
import { toStream } from '@/utils/mobx-util'
import { tap } from 'rxjs'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'

/**
 * Represents the shape of amendment visibility options.
 */
export interface AmendmentVisibilityOption {
  displayName: string
  visibility: AmendmentVisibility
}

/**
 * View Model for the Create Amendment Dialog.
 */
export class CreateAmendmentDialogViewModel implements ActivationLifecycle {
  /**
   * Holds the maximum length value for the description field.
   */
  static readonly MAX_DESCRIPTION_LENGTH = 500

  /**
   * Holds the maximum length value for the internal note field.
   */
  static readonly MAX_INTERNAL_NOTE_LENGTH = 250

  /**
   * Options for Amendment visibility selection.
   */
  static readonly VISIBILITY_OPTIONS: AmendmentVisibilityOption[] = [
    { displayName: 'Public', visibility: 'Public' },
    { displayName: 'Internal', visibility: 'Internal' },
  ]

  private readonly _lifecycle = new LifecycleTracker()

  readonly modal = new ModalViewModel({ onClose: () => this.reset() })

  /**
   * Description of the amendment.
   */
  @observable accessor description = ''

  /**
   * Internal note of the amendment.
   */
  @observable accessor internalNote = ''

  /**
   * Visibility of the amendment.
   */
  @observable accessor visibility: AmendmentVisibility | null = null

  /**
   * Amendment Client amount.
   */
  @observable accessor clientAmount = 0

  /**
   * Amendment Provider amount.
   */
  @observable accessor providerAmount = 0

  /**
   * Validation context for this view model.
   */
  readonly validation = validationContext(this, {
    description: [
      required(
        'Description is required, the Client/Pro should know what they are paying/getting paid for.',
      ),
      mustHaveMaxLength(
        CreateAmendmentDialogViewModel.MAX_DESCRIPTION_LENGTH,
        'Amendment description must be less than or equal to 500 characters.',
      ),
    ],
    internalNote: [
      required('Internal note is required.'),
      mustHaveMaxLength(
        CreateAmendmentDialogViewModel.MAX_INTERNAL_NOTE_LENGTH,
        'Amendment internal note must be less than or equal to 250 characters.',
      ),
    ],
    visibility: [required('Visibility is required.')],
  })

  constructor(
    private readonly _job: Job,
    private readonly _canViewAmendments: () => boolean,
    private readonly _jobStore: JobStore,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {
    this._clearValidationOnChange('description')
    this._clearValidationOnChange('internalNote')
    this._clearValidationOnChange('visibility')
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * The currently selected amendment visibility option.
   */
  @computed
  get option() {
    return CreateAmendmentDialogViewModel.VISIBILITY_OPTIONS.find(
      (o) => o.visibility === this.visibility,
    )
  }

  /**
   * Sets the amendment visibility.
   */
  @action.bound
  setVisibilityOption(visibilityOption?: AmendmentVisibilityOption) {
    if (visibilityOption) {
      this.visibility = visibilityOption.visibility
    }
  }

  /**
   * Clears the fields of the View Model.
   */
  @action.bound
  reset() {
    this.description = ''
    this.internalNote = ''
    this.clientAmount = 0
    this.providerAmount = 0
    this.visibility = null

    this.createAmendment.reset()
    this.validation.reset()
  }

  /**
   * Indicates if the View Model is valid.
   */
  @action.bound
  validate() {
    return this.validation.reset().validate().isValid
  }

  /**
   * Creates an amendment.
   */
  createAmendment = task.resolved(async () => {
    const alert = this._newAlert()

    if (!this._canViewAmendments()) {
      alert
        .withState('error')
        .saying('You do not have permission to perform this action.')
        .autoDismiss()
        .show()
      return
    }

    if (!this.validate()) return

    if (this.visibility == null) return

    try {
      alert
        .withState('pending')
        .saying(
          'Creating amendment',
          `Adding amendment to ${this._job.name}...`,
        )
        .autoDismiss()
        .show()

      await this._jobStore.createAmendment({
        jobId: this._job.id,
        description: this.description,
        internalNote: this.internalNote,
        visibility: this.visibility,
        clientAmount: this.clientAmount,
        providerAmount: this.providerAmount,
      })
    } catch (err) {
      alert.dismiss()
      this._createAlertErrorHandler('Creating the amendment failed')(err)
    }

    alert
      .withState('success')
      .saying('Success!', `The amendment was added to ${this._job.name}.`)
      .autoDismiss()

    this.modal.close()
  })

  /**
   * Shows add amendment dialog.
   */
  @action.bound
  show() {
    if (this.modal.showing) {
      return
    }

    this.modal.show()
  }

  /**
   * Sets up automatic clearing of validation errors when the field changes.
   */
  private _clearValidationOnChange(field: keyof this) {
    this._lifecycle.manage(
      toStream(() => this[field]).pipe(
        whileActive(this.modal.showing$),
        tap(() => this.validation.clearErrors(field as string)),
      ),
    )
  }
}
